/**
 * ExplainResultPanel — "How this scenario priced", as a floating panel pinned
 * to the bottom-right of the window rather than a modal over the page. The
 * results stay readable beside it, and it can be minimized to its header.
 *
 *   ┌ (?) How this scenario priced              –  × ┐
 *   │     v3 · 8.500% · Oct 9, 1:20 PM                │
 *   ├─────────────────────────────────────────────────┤
 *   │ ✓ Fits the guidelines as priced.                │  verdict (plain terms)
 *   │   • Priced at an 8.500% coupon …                │
 *   │ RATE BUILD-UP                                   │  the additive `rate` block
 *   │ FINDINGS                       0 FAIL · 1 NOTE  │  exceptions + leverage checks
 *   │ REPRICE OPTIONS                                 │  `suggestions[]` (when wired)
 *   ├─────────────────────────────────────────────────┤
 *   │ disclaimer                                      │
 *   └─────────────────────────────────────────────────┘
 *
 * Shared by the RTL and GUC sizers — both price on `POST /pricing/scenario/rtl`
 * and get the same response shape. RENDER-ONLY: every line is a response field.
 *   - verdict        → `plainLines(result)` (the GUC plain-terms generator)
 *   - rate build-up  → `rate.baseRate12mo` (fraction) + the `*AdjPct` fields
 *                      (already in PERCENT) + `rate.accountSpread` (fraction),
 *                      totalling `rate.requiredCoupon`
 *   - findings       → `guidelineExceptions[]` (hard / soft / info, with
 *                      `citation`), then one pass/fail line per `leverage[]` row
 *                      using the engine's own `over` flag and `maximum`
 *   - reprice rows   → `suggestions[]`, only when `onRepriceAt` is passed
 * Request-exception buttons show only when `onRequestException` is passed.
 */
import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import ExpandLessRounded from "@mui/icons-material/ExpandLessRounded";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { status as STATUS } from "../../../theme/colors";
import { SZ, SecondaryButton } from "../sizerUi";
import { fmtAdjPct, fmtMoney, fmtPct, fmtRate, humanize } from "../sizerFormat";
import { rtlRequiredCoupon } from "../rtlRateDisplay";
import { isInfoFinding } from "../../GucSizerScreen/gucConstants";
import { plainLines, verdict } from "../../GucSizerScreen/gucPlainSummary";
import { NUM } from "./workspaceParts";

const PANEL_W = 460;

/* ============================================================ Pieces */

// `right` = an uppercase count ("0 fail · 1 note"); `note` = a plain aside.
const SectionHead = ({ children, right, note }) => (
  <Flx jb ac g={1} sx={{ mt: 2.5, mb: 1 }}>
    <Txt
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        color: SZ.ink500,
      }}
    >
      {children}
    </Txt>
    {note && <Txt sx={{ fontSize: 11.5, color: SZ.ink400 }}>{note}</Txt>}
    {right && (
      <Txt
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: ".06em",
          textTransform: "uppercase",
          color: SZ.ink500,
          ...NUM,
        }}
      >
        {right}
      </Txt>
    )}
  </Flx>
);

const Group = ({ children }) => (
  <Box
    sx={{
      border: `1px solid ${SZ.line}`,
      borderRadius: SZ.radiusSm,
      overflow: "hidden",
      bgcolor: SZ.card,
    }}
  >
    {children}
  </Box>
);

/** The verdict callout — green when it fits, red when something is breached. */
function Verdict({ result }) {
  const v = verdict(result) || {};
  const lines = plainLines(result);
  const good = v.tone === "good";
  const tone = good ? STATUS.success : STATUS.error;
  const Icon = good ? CheckCircleOutlineRounded : ErrorOutlineRounded;
  return (
    <Box
      sx={{
        border: `1px solid ${good ? "#A7F3D0" : "#FECACA"}`,
        bgcolor: good ? "#ECFDF5" : tone.bg,
        borderRadius: SZ.radiusSm,
        p: "12px 14px",
      }}
    >
      <Flx ac g={0.875}>
        <Icon sx={{ fontSize: 18, color: tone.fg }} />
        <Txt
          sx={{
            fontSize: 13.5,
            fontWeight: 700,
            color: good ? tone.fg : STATUS.error.title,
          }}
        >
          {lines[0]}
        </Txt>
      </Flx>
      {lines.length > 1 && (
        <Box
          component="ul"
          sx={{
            m: "8px 0 0",
            pl: "22px",
            color: good ? SZ.ink700 : STATUS.error.body,
            "& li": { fontSize: 12.5, lineHeight: 1.55, mb: "2px" },
          }}
        >
          {lines.slice(1).map((l, i) => (
            <li key={i}>{l}</li>
          ))}
        </Box>
      )}
    </Box>
  );
}

const BuildRow = ({ k, v, total }) => (
  <Flx
    jb
    g={1}
    sx={{
      p: total ? "10px 14px" : "8px 14px",
      borderTop: total ? `1px solid ${SZ.lineStrong}` : `1px solid ${SZ.line}`,
      "&:first-of-type": { borderTop: 0 },
      bgcolor: total ? "#fbfcfc" : "transparent",
    }}
  >
    <Txt sx={{ fontSize: 13, fontWeight: total ? 700 : 400, color: SZ.ink900 }}>
      {k}
    </Txt>
    <Txt
      sx={{
        fontFamily: SZ.mono,
        fontSize: 13,
        fontWeight: total ? 700 : 500,
        color: SZ.ink900,
        ...NUM,
      }}
    >
      {v}
    </Txt>
  </Flx>
);

/** The additive rate block, line by line, ending on the required coupon. */
function RateBuildUp({ result, spreadLabel }) {
  const rate = result.rate || {};
  if (rate.error) {
    return (
      <Txt component="div" sx={{ fontSize: 12.5, color: SZ.warn }}>
        {rate.error}
      </Txt>
    );
  }
  const cls = result.resolved?.borrowerClass;
  const rows = [
    rate.baseRate12mo != null && {
      k: "Base rate",
      v: fmtRate(rate.baseRate12mo),
    },
    rate.termAdjPct != null && {
      k: result.termMonths ? `Term (${result.termMonths} mo)` : "Term",
      v: fmtAdjPct(rate.termAdjPct),
    },
    rate.leverageAdjPct != null && {
      k: rate.leverageBand ? `Leverage (${rate.leverageBand})` : "Leverage",
      v: fmtAdjPct(rate.leverageAdjPct),
    },
    rate.classAdjPct != null && {
      k: cls ? `Class ${cls}` : "Class",
      v: fmtAdjPct(rate.classAdjPct),
    },
    rate.loanAmountUptickPct != null &&
      Number(rate.loanAmountUptickPct) !== 0 && {
        k: rate.uptickTier ? `Loan amount (${rate.uptickTier})` : "Loan amount",
        v: fmtAdjPct(rate.loanAmountUptickPct),
      },
    rate.exceptionAdjPct != null &&
      Number(rate.exceptionAdjPct) !== 0 && {
        k: "Exception",
        v: fmtAdjPct(rate.exceptionAdjPct),
      },
    rate.accountSpread != null && {
      k: spreadLabel ? `Spread (${spreadLabel})` : "Spread",
      // A fraction (0.0025), unlike the *AdjPct fields above.
      v: fmtAdjPct(Number(rate.accountSpread) * 100),
    },
  ].filter(Boolean);
  return (
    <Group>
      {rows.map((r) => (
        <BuildRow key={r.k} k={r.k} v={r.v} />
      ))}
      <BuildRow
        total
        k="Required coupon"
        v={fmtRate(rtlRequiredCoupon(rate))}
      />
    </Group>
  );
}

const SEV = {
  hard: { icon: CancelOutlined, color: STATUS.error.fg, label: "Blocker" },
  soft: { icon: ErrorOutlineRounded, color: STATUS.warning.fg, label: "Soft" },
  info: { icon: InfoOutlined, color: SZ.ink400, label: "Note" },
  pass: { icon: CheckCircleOutlineRounded, color: SZ.good, label: null },
};

const RuleChip = ({ children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      mt: "6px",
      fontFamily: SZ.mono,
      fontSize: 11,
      color: SZ.ink500,
      bgcolor: SZ.bg,
      border: `1px solid ${SZ.line}`,
      borderRadius: "5px",
      px: "7px",
      lineHeight: "18px",
    }}
  >
    {children}
  </Box>
);

function FindingRow({ kind, message, citation, approver, action }) {
  const s = SEV[kind] || SEV.soft;
  const Icon = s.icon;
  return (
    <Box
      sx={{
        border: `1px solid ${SZ.line}`,
        borderRadius: SZ.radiusSm,
        p: "11px 13px",
        mb: 1,
        bgcolor: SZ.card,
      }}
    >
      <Flx g={1} sx={{ alignItems: "flex-start" }}>
        <Icon sx={{ fontSize: 17, color: s.color, mt: "1px", flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Txt
            component="div"
            sx={{ fontSize: 13, color: SZ.ink900, lineHeight: 1.45 }}
          >
            {message}
          </Txt>
          <Flx ac g={1} sx={{ flexWrap: "wrap" }}>
            {citation && <RuleChip>Rule · {citation}</RuleChip>}
            {s.label && kind !== "pass" && (
              <Txt
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: s.color,
                  mt: "6px",
                }}
              >
                {s.label}
                {approver ? ` · approver: ${humanize(approver)}` : ""}
              </Txt>
            )}
          </Flx>
          {action && <Box sx={{ mt: 1 }}>{action}</Box>}
        </Box>
      </Flx>
    </Box>
  );
}

const PendingChip = () => (
  <Txt
    sx={{
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: ".04em",
      textTransform: "uppercase",
      p: "3px 9px",
      borderRadius: "99px",
      bgcolor: SZ.warnBg,
      color: SZ.warn,
    }}
  >
    Requested · pending
  </Txt>
);

const smallBtn = { height: 30, fontSize: 12, px: "12px" };

/* ============================================================ Panel */

export default function ExplainResultPanel({
  open,
  onClose,
  result,
  version,
  pricedAt,
  spreadLabel,
  // Shown in the footer, as the old GUC dialog did under its title.
  scenarioId,
  pendingCodes = [],
  onRequestException,
  onRepriceAt,
}) {
  const [minimized, setMinimized] = useState(false);
  useEffect(() => {
    if (open) setMinimized(false);
  }, [open]);

  // Esc closes it, like the dialog it replaced.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !result) return null;

  const exceptions = result.guidelineExceptions || [];
  const leverage = (result.leverage || []).filter((r) => r?.metric);
  const cls = result.resolved?.borrowerClass;
  const pending = new Set(pendingCodes);
  // Facility hard checks and GATE-mode takeout denials are findings too — they
  // are what can raise the hard banner when no guideline row is hard, so the
  // panel must list them or the banner points at an empty explanation. They
  // carry no exception code, so they explain; they get no Request button.
  const facilityFails = (
    result.facilityHardExceptions?.failed_checks || []
  ).map((f) =>
    typeof f === "string"
      ? f
      : f?.message || f?.reason || f?.check || JSON.stringify(f),
  );
  const takeoutDenials =
    result.guidelineCheck?.mode === "gate"
      ? (result.guidelineCheck?.takeout_denials || []).map((d) =>
          typeof d === "string"
            ? d
            : d?.message || d?.reason || d?.rule || JSON.stringify(d),
        )
      : [];
  const fails =
    exceptions.filter((f) => !isInfoFinding(f)).length +
    facilityFails.length +
    takeoutDenials.length;
  const notes = exceptions.length - fails;
  const suggestions = onRepriceAt ? result.suggestions || [] : [];
  const metricRows = suggestions.filter((s) => s.metric);
  const altRows = suggestions.filter((s) => s.type);

  const sub = [
    version ? `v${version}` : null,
    fmtRate(rtlRequiredCoupon(result.rate || {})),
    pricedAt ? formatDateTime(pricedAt, "MMM d, h:mm a") : null,
  ]
    .filter((x) => x && x !== "—")
    .join(" · ");

  return (
    <Box
      role="dialog"
      aria-label="How this scenario priced"
      sx={{
        position: "fixed",
        right: { xs: 12, md: 24 },
        bottom: { xs: 12, md: 24 },
        zIndex: 1250,
        width: PANEL_W,
        maxWidth: "calc(100vw - 24px)",
        maxHeight: minimized ? "none" : "min(680px, calc(100vh - 120px))",
        display: "flex",
        flexDirection: "column",
        bgcolor: SZ.card,
        border: `1px solid ${SZ.line}`,
        borderRadius: "14px",
        boxShadow:
          "0 18px 48px rgba(16,28,34,.18), 0 2px 6px rgba(16,28,34,.06)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Flx
        ac
        g={1.25}
        sx={{
          p: "14px 14px 12px 16px",
          borderBottom: minimized ? 0 : `1px solid ${SZ.line}`,
        }}
      >
        <Flx
          center
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: "8px",
            bgcolor: SZ.teal50,
            color: SZ.teal700,
            "& svg": { fontSize: 18 },
          }}
        >
          <HelpOutlineRounded />
        </Flx>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Txt
            component="div"
            sx={{ fontSize: 15, fontWeight: 700, color: SZ.ink900 }}
          >
            How this scenario priced
          </Txt>
          {sub && (
            <Txt
              component="div"
              sx={{
                fontFamily: SZ.mono,
                fontSize: 11.5,
                color: SZ.ink500,
                mt: "1px",
                ...NUM,
              }}
            >
              {sub}
            </Txt>
          )}
        </Box>
        <Tooltip title={minimized ? "Expand" : "Minimize"}>
          <IconButton
            size="small"
            onClick={() => setMinimized((m) => !m)}
            aria-label={minimized ? "Expand" : "Minimize"}
          >
            {minimized ? (
              <ExpandLessRounded sx={{ fontSize: 19 }} />
            ) : (
              <RemoveRounded sx={{ fontSize: 19 }} />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseRounded sx={{ fontSize: 19 }} />
          </IconButton>
        </Tooltip>
      </Flx>

      {!minimized && (
        <>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              p: "14px 16px 16px",
              bgcolor: "#fcfdfd",
            }}
          >
            <Verdict result={result} />

            <SectionHead>Rate build-up</SectionHead>
            <RateBuildUp result={result} spreadLabel={spreadLabel} />

            <SectionHead right={`${fails} fail · ${notes} note`}>
              Findings
            </SectionHead>
            {exceptions.map((f, i) => {
              const info = isInfoFinding(f);
              const kind = info
                ? "info"
                : f.severity === "hard"
                  ? "hard"
                  : "soft";
              return (
                <FindingRow
                  key={`${f.code}-${i}`}
                  kind={kind}
                  message={f.message}
                  citation={f.citation}
                  approver={info ? null : f.approver}
                  action={
                    !info && onRequestException ? (
                      pending.has(f.code) ? (
                        <PendingChip />
                      ) : (
                        <SecondaryButton
                          onClick={() => onRequestException(f)}
                          sx={smallBtn}
                        >
                          Request exception
                        </SecondaryButton>
                      )
                    ) : null
                  }
                />
              );
            })}
            {facilityFails.map((m, i) => (
              <FindingRow
                key={`facility-${i}`}
                kind="hard"
                message={`Facility: ${m}`}
              />
            ))}
            {takeoutDenials.map((m, i) => (
              <FindingRow
                key={`takeout-${i}`}
                kind="hard"
                message={`Takeout: ${m}`}
              />
            ))}
            {leverage.map((r) => (
              <FindingRow
                key={r.metric}
                kind={r.over ? "hard" : "pass"}
                message={
                  r.maximum == null
                    ? `${r.metric} ${fmtPct(r.requested, 2)} — no maximum applies.`
                    : r.over
                      ? `${r.metric} ${fmtPct(r.requested, 2)} is over the ${fmtPct(r.maximum, 2)} max${cls ? ` for Class ${cls}` : ""}.`
                      : `${r.metric} ${fmtPct(r.requested, 2)} is within the ${fmtPct(r.maximum, 2)} max${cls ? ` for Class ${cls}` : ""}.`
                }
              />
            ))}
            {!exceptions.length && !leverage.length && (
              <Txt component="div" sx={{ fontSize: 12.5, color: SZ.ink500 }}>
                No guideline findings were returned for this scenario.
              </Txt>
            )}

            {(metricRows.length > 0 || altRows.length > 0) && (
              <>
                <SectionHead note="each writes the form input it names">
                  Reprice options
                </SectionHead>
                {metricRows.map((r) => (
                  <FindingRow
                    key={r.metric}
                    kind="info"
                    message={`${r.metric} at its maximum — ${fmtPct(r.feasibleLeverage, 2)} rather than ${fmtPct(r.requestedLeverage, 2)}. Total loan ${fmtMoney(r.maxLoanAtMax)} · initial advance ${fmtMoney(r.baseLoanAmountAtMax)}.`}
                    action={
                      <SecondaryButton
                        onClick={() => onRepriceAt(r.baseLoanAmountAtMax)}
                        sx={smallBtn}
                      >
                        Re-price at this amount
                      </SecondaryButton>
                    }
                  />
                ))}
                {altRows.map((r) => (
                  <FindingRow
                    key={r.type}
                    kind={r.passing ? "pass" : "soft"}
                    message={`${humanize(r.type)} — advance ${fmtMoney(r.baseLoanAmount)}, total ${fmtMoney(r.totalLoanAmount)}. ${r.passing ? "Measured as passing." : "Measured as still over."}${
                      r.leverage
                        ? ` ${Object.entries(r.leverage)
                            .map(([k, v]) => `${k} ${fmtPct(v, 2)}`)
                            .join(" · ")}`
                        : ""
                    }`}
                    action={
                      <SecondaryButton
                        onClick={() => onRepriceAt(r.baseLoanAmount)}
                        sx={smallBtn}
                      >
                        Re-price at this amount
                      </SecondaryButton>
                    }
                  />
                ))}
              </>
            )}
          </Box>

          {(result.disclaimer || scenarioId) && (
            <Box
              sx={{
                borderTop: `1px solid ${SZ.line}`,
                p: "10px 16px 12px",
                bgcolor: SZ.card,
              }}
            >
              <Txt
                component="div"
                sx={{
                  fontSize: 11.5,
                  color: SZ.ink400,
                  fontStyle: "italic",
                  lineHeight: 1.5,
                }}
              >
                {result.disclaimer}
              </Txt>
              {scenarioId && (
                <Txt
                  component="div"
                  sx={{
                    fontFamily: SZ.mono,
                    fontSize: 10.5,
                    color: SZ.ink400,
                    mt: result.disclaimer ? "6px" : 0,
                  }}
                >
                  Scenario {scenarioId}
                </Txt>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

/**
 * The "The full explanation" strip at the top of Review — the way into the
 * panel. Same card on every sizer that has an explanation to give.
 */
export function ExplainCard({ onExplain, description }) {
  return (
    <Flx
      jb
      ac
      g={1.5}
      sx={{
        bgcolor: SZ.card,
        border: `1px solid ${SZ.line}`,
        borderRadius: SZ.radius,
        p: "12px 16px",
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
        <Txt
          component="div"
          sx={{ fontSize: 13, fontWeight: 600, color: SZ.ink900 }}
        >
          The full explanation
        </Txt>
        <Txt component="div" sx={{ fontSize: 12, color: SZ.ink500, mt: "2px" }}>
          {description ||
            "Every finding with its citation, the rate build-up, and the reprice options the engine returned."}
        </Txt>
      </Box>
      <SecondaryButton
        onClick={onExplain}
        startIcon={<HelpOutlineRounded />}
        sx={{
          height: 36,
          minHeight: 36,
          padding: "0 14px",
          fontSize: 13,
          "& svg": { fontSize: 16 },
        }}
      >
        Explain this result
      </SecondaryButton>
    </Flx>
  );
}
